// Render-time sanitizer for a subscription plan's description (the rich-text
// field a Super Admin edits on the Plans & Pricing page and the PUBLIC
// /pricing card shows).
//
// Third pass after the editor's own tag set and the API's nh3 sanitize-on-write
// (api/src/utils/rich_text_sanitizer.py `sanitize_plan_description`): the
// column is craftable directly, so every v-html sink re-sanitizes. The
// allowlist is exactly the editor toolbar -- bold, italic, lists, line break,
// link -- and the tag set MUST stay equal to the backend's. No attributes
// except a link's href; no style/class/id/target/data-*/on*.
import DOMPurify from "dompurify";

export const PLAN_DESCRIPTION_MAX_CHARS = 600;

const ALLOWED_TAGS = ["p", "br", "strong", "em", "ul", "ol", "li", "a"];
const ALLOWED_ATTR = ["href"];
// Full https or mailto URLs only: no http, tel, relative or protocol-relative.
const ALLOWED_URI = /^(?:https|mailto):/i;
const LINK_REL = "noopener noreferrer";
const AFTER_ATTRIBUTES_HOOK = "afterSanitizeAttributes";

function linkRelHook(node) {
  if (node && node.tagName === "A") node.setAttribute("rel", LINK_REL);
}

// An anchor whose href was stripped (javascript:, http:, relative) is dead
// text in link clothing: unwrap it, keeping its children.
function unwrapHreflessAnchors(html) {
  if (!/<a[\s>]/i.test(html)) return html;
  const box = document.createElement("div");
  box.innerHTML = html;
  box.querySelectorAll("a:not([href])").forEach((a) => {
    a.replaceWith(...a.childNodes);
  });
  return box.innerHTML;
}

/**
 * Sanitize plan-description HTML (or legacy plain text) for v-html.
 * Returns "" for null / empty / non-string input.
 */
export function sanitizePlanDescription(dirty) {
  if (!dirty || typeof dirty !== "string") return "";
  DOMPurify.addHook(AFTER_ATTRIBUTES_HOOK, linkRelHook);
  try {
    const clean = DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOWED_URI_REGEXP: ALLOWED_URI,
      ALLOW_UNKNOWN_PROTOCOLS: false,
      ALLOW_DATA_ATTR: false,
    });
    return unwrapHreflessAnchors(clean);
  } finally {
    DOMPurify.removeHook(AFTER_ATTRIBUTES_HOOK, linkRelHook);
  }
}

/** Visible text of (sanitized) description HTML: markup excluded. */
export function planDescriptionText(html) {
  if (!html || typeof html !== "string") return "";
  const el = document.createElement("div");
  el.innerHTML = sanitizePlanDescription(html);
  return (el.textContent || "").replace(/\u00a0/g, " ").trim();
}

/**
 * Visible character count -- the unit the 600 cap is measured in. Counts
 * CODE POINTS (like the server's Python len), not UTF-16 units, so an emoji
 * is one character on both sides.
 */
export function planDescriptionLength(html) {
  return Array.from(planDescriptionText(html)).length;
}

/** True when the value carries any tag (rich text) vs legacy plain text. */
export function isRichDescription(value) {
  return typeof value === "string" && /<\/?[a-z][^>]*>/i.test(value);
}
